import { createContext, FC, ReactNode, useCallback, useContext, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { E164Number } from 'libphonenumber-js'
import { useToast } from '@/molecules/Toast/ToastProvider'
import { useVenueList } from '@/organisms/TheaterPreferences/hooks/useVenueList'
import { useUser } from '@/services/UserService'
import { useSafeAnalytics } from '@/utils/analytics'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { UPSERT_THEATRICAL_USER_PREFERENCES } from './mutations'
import { GET_THEATRICAL_USER_PREFERENCES } from './queries'
import {
  Coordinates,
  TheaterInfo,
  TheaterPreferencesContextType,
  TheaterPreferencesVenue,
  UserPreferenceInput,
} from './types'
import { getAddressString, processDayPreferences, transformDayPreferencesToApi } from './utils'

const TheaterPreferencesContext = createContext<TheaterPreferencesContextType | undefined>(undefined)

interface TheaterPreferencesProviderProps {
  children: ReactNode
}
// TODO: MAKE SURE THE ERROR HANDLING AND TOASTS ARE CORRECT
export const TheaterPreferencesProvider: FC<TheaterPreferencesProviderProps> = ({ children }) => {
  const { t } = useTranslate('account')
  const [location, setLocation] = useState<string>('')
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null)
  const [selectedVenueId, setSelectedVenueId] = useState<string>('')
  const [selectedTheater, setSelectedTheater] = useState<TheaterInfo | null>(null)
  const [dayTimePreferences, setDayTimePreferences] = useState<Record<string, string[]>>({})
  const [phoneNumber, setPhoneNumber] = useState<E164Number | undefined>()
  const [autobookEnabled, setAutobookEnabled] = useState<boolean>(false)
  const { showToast } = useToast()
  const {
    venues,
    loading: venuesLoading,
    userLocation,
    error: venuesError,
  } = useVenueList({
    coordinates: coordinates || undefined,
  })
  const { isLoggedIn } = useUser()
  const { track } = useSafeAnalytics()
  const { query } = useSafeRouter()
  const campaign = query?.utm_campaign || ''
  const source = query?.utm_source || ''
  const medium = query?.utm_medium || ''
  const isAutobookFlow = query?.isAutobookFlow || false

  const [upsertPreferences, { loading }] = useMutation(UPSERT_THEATRICAL_USER_PREFERENCES, {
    onCompleted: (data) => {
      if (data?.theatricalUserPreferences?.id) {
        const { dayPreferences, preferredVenues } = data.theatricalUserPreferences
        if (dayPreferences?.length > 0) {
          setDayTimePreferences(processDayPreferences(dayPreferences))
        }
        if (data?.theatricalUserPreferences?.phoneNumber) {
          setPhoneNumber(data.theatricalUserPreferences.phoneNumber)
        }

        if (preferredVenues?.[0]?.providerId) {
          const venue = preferredVenues[0]
          setSelectedVenueId(venue.providerId)
          setSelectedTheater({
            ...venue,
            address: getAddressString(venue, ''),
          })
        }
      }
    },
    onError: (error) => {
      showToast(t('failedToUpdateTheaterPreferences', 'Failed to update theater preferences'))
      logger().error('Error updating theater preferences:', error)
    },
    errorPolicy: 'all',
  })

  const { data: preferencesData, loading: preferencesLoading } = useQuery(GET_THEATRICAL_USER_PREFERENCES, {
    onCompleted: (data) => {
      if (data?.theatricalUserPreferences?.id) {
        const { dayPreferences, preferredVenues } = data.theatricalUserPreferences
        if (dayPreferences?.length > 0) {
          setDayTimePreferences(processDayPreferences(dayPreferences))
        }
        if (data?.theatricalUserPreferences?.phoneNumber) {
          setPhoneNumber(data.theatricalUserPreferences.phoneNumber)
        }
        if (data?.theatricalUserPreferences?.autobookEnabled) {
          setAutobookEnabled(data.theatricalUserPreferences.autobookEnabled)
        }

        if (preferredVenues?.[0]?.providerId) {
          const venue = preferredVenues[0]
          setSelectedVenueId(venue.providerId)
          setSelectedTheater({
            ...venue,
            address: getAddressString(venue, ''),
          })
        }
      }
    },
    onError: (error) => {
      logger().error('Error fetching theater preferences:', error)
    },
    skip: !isLoggedIn,
  })

  const handleAddressChange = useCallback((coords: Coordinates, searchString: string) => {
    setLocation(searchString)
    setCoordinates(coords)
    setSelectedVenueId('')
    setSelectedTheater(null)
  }, [])

  const handleVenueSelect = useCallback(
    (venueId: string, venueData?: TheaterPreferencesVenue) => {
      setSelectedVenueId(venueId)
      const cityName = location.split(',')[0] || null
      setSelectedTheater({
        id: venueId,
        name: venueData?.name || `${cityName || 'Selected'} Theater`,
        address: getAddressString(venueData, location),
        distance: venueData?.distance?.toString(),
        phone: venueData?.phone || undefined,
      })
    },
    [location],
  )

  const handleDayTimeToggle = useCallback((dayId: string, timeWindowId: string) => {
    setDayTimePreferences((prev) => {
      if (!timeWindowId) {
        const { [dayId]: _, ...rest } = prev
        return dayId in prev ? rest : { ...prev, [dayId]: [] }
      }

      const dayTimes = prev[dayId] || []
      const updatedDayTimes = dayTimes.includes(timeWindowId)
        ? dayTimes.filter((id) => id !== timeWindowId)
        : [...dayTimes, timeWindowId]

      return {
        ...prev,
        [dayId]: updatedDayTimes.length > 0 ? updatedDayTimes : [],
      }
    })
  }, [])

  const handleSave = useCallback(async () => {
    if (!selectedVenueId) {
      showToast(t('pleaseSelectTheaterBeforeSaving', 'Please select a theater before saving'))
      return
    }

    const dayPreferences = transformDayPreferencesToApi(dayTimePreferences)
    const input: UserPreferenceInput = {
      theaterPreferences: [
        {
          id: selectedVenueId,
          priority: 0,
        },
      ],
      autobookEnabled,
      phoneNumber,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(dayPreferences.length > 0 && { dayPreferences }),
    }

    try {
      await upsertPreferences({
        variables: { input },
      })
      if (autobookEnabled && isAutobookFlow) {
        track('Save My Seats Sign Up - preferences saved', { campaign, source, medium })
      }
    } catch (err) {
      logger().error('Error calling mutation:', err, input)
    }
  }, [
    phoneNumber,
    autobookEnabled,
    selectedVenueId,
    dayTimePreferences,
    t,
    isAutobookFlow,
    showToast,
    upsertPreferences,
    track,
    campaign,
    source,
    medium,
  ])

  const handleSaveVenuePreferenceOnly = useCallback(
    async (venueId: string) => {
      if (!venueId) {
        showToast(t('pleaseSelectTheaterBeforeSaving', 'Please select a theater before saving'))
        return
      }

      const input: UserPreferenceInput = {
        theaterPreferences: [
          {
            id: venueId,
            priority: 0,
          },
        ],
      }

      try {
        await upsertPreferences({
          variables: { input },
        })
        showToast(t('preferredTheaterSet', 'Preferred theater set'))
      } catch (err) {
        logger().error('Error calling mutation:', err, input)
      }
    },
    [t, showToast, upsertPreferences],
  )

  const value = {
    preferencesData,
    defaultAddressString: userLocation?.city
      ? `${userLocation?.city}, ${userLocation?.state} ${userLocation?.zipCode}`
      : '',
    location,
    coordinates,
    selectedVenueId,
    selectedTheater,
    dayTimePreferences,
    autobookEnabled,
    hasExistingPreferences: Boolean(preferencesData?.theatricalUserPreferences?.id),
    preferencesLoading,
    loading,
    venues,
    venuesLoading,
    venuesError: venuesError || null,
    userLocation,
    phoneNumber,
    setPhoneNumber,
    setAutobookEnabled,
    handleAddressChange,
    handleVenueSelect,
    handleDayTimeToggle,
    handleSave,
    handleSaveVenuePreferenceOnly,
  }

  return <TheaterPreferencesContext.Provider value={value}>{children}</TheaterPreferencesContext.Provider>
}

export const useTheaterPreferences = (): TheaterPreferencesContextType => {
  const context = useContext(TheaterPreferencesContext)
  if (context === undefined) {
    throw new Error('useTheaterPreferences must be used within a TheaterPreferencesProvider')
  }
  return context
}
